import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { UserProfile } from '../types';

interface LoginAlertsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  activeAlerts: { sfi: boolean; dues: boolean };
  onDismissAlert: (type: 'sfi' | 'dues', permanently: boolean) => void;
}

export default function LoginAlertsModal({
  isOpen,
  onClose,
  profile,
  activeAlerts,
  onDismissAlert,
}: LoginAlertsModalProps) {
  if (!isOpen) return null;

  const currentYear = new Date().getFullYear();

  return (
    <div className="fixed inset-0 bg-[var(--background)]/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-[var(--background)] border-2 border-lcars-red rounded-xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 shadow-2xl shadow-lcars-red/20">
        
        {/* LCARS Header */}
        <div className="flex h-12">
          <div className="w-16 bg-lcars-red rounded-br-full shrink-0"></div>
          <div className="flex-1 flex justify-between items-center px-4 bg-lcars-red/10 border-b border-lcars-red">
            <h2 className="text-xl font-bold uppercase tracking-widest text-lcars-red flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> LCARS SYSTEM ALERT
            </h2>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-lcars-red/20 text-lcars-red rounded transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto max-h-[70vh] space-y-6">
          <p className="text-lcars-gold/90 text-sm uppercase tracking-widest leading-relaxed">
            The computer has detected pending administrative requirements for your personnel file. Please address these updates:
          </p>

          <div className="space-y-6">
            
            {activeAlerts.sfi && (
              <div className="bg-lcars-red/5 p-5 rounded border-l-4 border-lcars-red space-y-4">
                <div className="space-y-1">
                  <h3 className="font-bold text-lcars-red uppercase tracking-widest text-base">SFI Membership Expiration</h3>
                  <p className="text-sm text-gray-300">
                    Your Starfleet International (SFI) membership is expiring soon or has expired.
                  </p>
                  <p className="text-xs text-lcars-gold/70 font-mono">
                    CURRENT EXPIRATION DATE: {profile.sfiExpireDate || 'NOT CONFIGURED'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    onClick={() => onDismissAlert('sfi', false)}
                    className="h-10 px-4 bg-lcars-tan hover:bg-lcars-tan/80 text-black rounded text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    Remind me next time
                  </button>
                  <button
                    onClick={() => onDismissAlert('sfi', true)}
                    className="h-10 px-4 bg-lcars-red hover:bg-lcars-red/80 text-[var(--foreground)] rounded text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    Don't remind me again
                  </button>
                </div>
              </div>
            )}

            {activeAlerts.dues && (
              <div className="bg-lcars-gold/5 p-5 rounded border-l-4 border-lcars-gold space-y-4">
                <div className="space-y-1">
                  <h3 className="font-bold text-lcars-gold uppercase tracking-widest text-base">December Chapter Dues</h3>
                  <p className="text-sm text-gray-300">
                    Annual chapter dues for the upcoming cycle are due in December. Please submit your dues to the chapter treasurer.
                  </p>
                  <p className="text-xs text-lcars-gold/70 font-mono font-readability">
                    STATUS FOR {currentYear}: UNPAID / OUT-OF-DATE
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    onClick={() => onDismissAlert('dues', false)}
                    className="h-10 px-4 bg-lcars-tan hover:bg-lcars-tan/80 text-black rounded text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    Remind me next time
                  </button>
                  <button
                    onClick={() => onDismissAlert('dues', true)}
                    className="h-10 px-4 bg-lcars-gold hover:bg-lcars-gold/80 text-black rounded text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    Don't remind me again
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* LCARS Footer */}
        <div className="flex h-8 mt-2">
          <div className="flex-1 bg-lcars-red/10 border-t border-lcars-red flex justify-end px-4 items-center">
            <span className="text-[0.5rem] text-lcars-red font-mono tracking-widest">SYSTEM ALERT STATUS ACTIVE</span>
          </div>
          <div className="w-16 bg-lcars-red rounded-tl-full shrink-0"></div>
        </div>
      </div>
    </div>
  );
}
